import React, { useState, useEffect, useCallback } from "react";
import { collection, addDoc, doc, writeBatch } from "firebase/firestore";
import { db } from "../services/firebase";
import { useBuyer } from "../contexts/BuyerContext";
import Header from "../components/Header";
import "./Selection.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useProductState, useProductActions } from "../contexts/ProductContext";
import { SelectionsSkeleton } from "../components/Skeleton";

const Selections = () => {
  const { buyer } = useBuyer();
  const navigate = useNavigate();
  const location = useLocation();
  const { removeFromSelected, clearAllSelected } = useProductActions();

  const { selectedProducts, loading } = useProductState();

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [paymentMethods, setPaymentMethods] = useState({});
  const [isSelectionHidden, setIsSelectionHidden] = useState(true);
  const [requestHUID, setRequestHUID] = useState(false);
  const [requestResultModal, setRequestResultModal] = useState(null);

  useEffect(() => {
    if (selectedProducts.length > 0) {
      const allIds = new Set(selectedProducts.map((p) => p.documentId));
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  }, [selectedProducts]);

  const logAreaDefaultOperators = useCallback(async () => {
    try {
      const { getDocs, collection } = await import("firebase/firestore");
      const areaDefaultSnap = await getDocs(
        collection(db, "AreaDefaultOperators"),
      );

      const operatorsByDistrict = {};
      // const buyerDistrict = buyer?.district;

      areaDefaultSnap.docs.forEach((doc) => {
        const data = doc.data();
        const { district, segment, operatorId, operatorName, mobile } = data;
        const districtKey = String(district || "")
          .trim()
          .toLowerCase();
        const segmentKey = String(segment || "")
          .trim()
          .toLowerCase();

        if (!districtKey || !segmentKey) return;

        if (!operatorsByDistrict[districtKey]) {
          operatorsByDistrict[districtKey] = {};
        }
        operatorsByDistrict[districtKey][segmentKey] = {
          operatorId,
          operatorName,
          mobile,
        };
      });

      return operatorsByDistrict;
    } catch (err) {
      return {};
    }
  }, []);

  useEffect(() => {
    logAreaDefaultOperators();
  }, [logAreaDefaultOperators]);

  const calculateProductWeights = (product) => {
    const num = (v) => parseFloat(v) || 0;

    const wastage =
      num(product?.buyerDeskCalculation) +
      num(product?.wastage) +
      num(product?.wastageOperator) +
      num(product?.wastageJmi) +
      num(product?.specificationMC) +
      (product?.specification && product.specification !== "Plane"
        ? (num(product?.specificationMC) *
            num(product?.specificationMakingJmi)) /
            100 +
          (num(product?.specificationMC) *
            num(product?.specificationMakingOperator)) /
            100
        : 0);

    const setMcBase = num(product?.setMc);
    const setMakingPercent =
      num(product?.setMakingJmi) + num(product?.setMakingOperator);
    const gramMcPerGram =
      num(product?.netGramMc) +
      num(product?.netGramMc) *
        ((num(product?.gramMakingJmi) + num(product?.gramMakingOperator)) /
          100);
    const specMcPerGram =
      num(product?.specificationGramRate) +
      num(product?.specificationGramRate) *
        ((num(product?.specificationGramRateJmi) +
          num(product?.specificationGramRateOperator)) /
          100);

    const totals = {
      totalGrossWt: 0,
      totalNetWt: 0,
      totalFineWt: 0,
      totalMC: 0,
      totalSets: 0,
    };

    if (
      Array.isArray(product.lotDetails) &&
      Array.isArray(product.selectedVariants)
    ) {
      product.selectedVariants.forEach((variant) => {
        const lot = product.lotDetails.find((l) => l.size === variant.size);
        if (!lot) {
          return;
        }

        const qty = num(variant.quantity);
        const avgNet = num(lot.avgNetWt);
        const avgGross = num(lot.avgGrossWt);
        const avgSpec = num(lot.avgSpecWt);

        totals.totalSets += qty;
        totals.totalNetWt += avgNet * qty;
        totals.totalGrossWt += avgGross * qty;
        totals.totalFineWt += ((avgNet * wastage) / 100) * qty;

        const lotSetMC = setMcBase + (setMcBase * setMakingPercent) / 100;
        const lotGramMC = avgNet * gramMcPerGram;
        const lotSpecMC = avgSpec * specMcPerGram;

        totals.totalMC += (lotSetMC + lotGramMC + lotSpecMC) * qty;
      });
    }
    return totals;
  };

  const getWeightDisplayText = (product) => {
    const weights = calculateProductWeights(product);
    return {
      grossText: `Gross: ~${weights.totalGrossWt.toFixed(3)} g`,
      netText: `Net: ~${weights.totalNetWt.toFixed(3)} g`,
      finewt: `Fine: ~${weights.totalFineWt.toFixed(3)} g`,
      mc: `Amount: ~₹ ${weights.totalMC.toFixed(2)}`,
      qtyText: `Qty: ${weights.totalSets}`,
    };
  };

  const calculateTotalWeights = () => {
    const selectedItems = selectedProducts
      .filter((product) =>
        !location?.state?.serviceType
          ? true
          : product.serviceType === location.state.serviceType,
      )
      .filter((p) => selectedIds.has(p.documentId));

    return selectedItems.reduce(
      (acc, product) => {
        const weights = calculateProductWeights(product);
        return {
          totalGross: acc.totalGross + weights.totalGrossWt,
          totalNet: acc.totalNet + weights.totalNetWt,
          totalSets: acc.totalSets + weights.totalSets,
          totalFineWt: acc.totalFineWt + weights.totalFineWt,
          totalMC: acc.totalMC + parseFloat(weights.totalMC || 0),
        };
      },
      { totalGross: 0, totalNet: 0, totalSets: 0, totalFineWt: 0, totalMC: 0 },
    );
  };

  const { totalGross, totalNet, totalSets, totalFineWt, totalMC } =
    calculateTotalWeights();

  const isAllSelected =
    selectedProducts.length > 0 && selectedIds.size === selectedProducts.length;

  const selectedItems = selectedProducts
    .filter((product) =>
      !location?.state?.serviceType
        ? true
        : product.serviceType === location.state.serviceType,
    )
    .filter((p) => selectedIds.has(p.documentId));
  const totalSelected = selectedItems.length;

  const handleSelectItem = (documentId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(documentId) ? next.delete(documentId) : next.add(documentId);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedIds(
      isAllSelected
        ? new Set()
        : new Set(selectedProducts.map((p) => p.documentId)),
    );
  };

  const handleRemoveItem = (documentId, productId) => {
    if (!documentId && !productId) {
      return;
    }
    removeFromSelected(documentId, buyer?.buyerId, productId);
  };

  const handlePaymentChange = (productId, method) => {
    setPaymentMethods((prev) => ({
      ...prev,
      [productId]: method,
    }));
  };

  // Fetch and console log all districts with default operators by segment

  const handlePlaceRequest = async () => {
    if (totalSelected === 0 || !buyer?.buyerId) return;
    try {
      // Log all area default operators by district & segment
      const operatorsByDistrict = await logAreaDefaultOperators();

      const selectedItemsToProcess = [...selectedItems];
      const num = (v) => parseFloat(v) || 0;
      const buyerDistrict = String(
        buyer?.buyerDistrict || buyer?.district || "",
      ).trim();
      const buyerDistrictKey = buyerDistrict.toLowerCase();

      // Skip only items without a valid operator; continue with assigned items.
      const itemsToProcess = [];
      const missingAssignments = [];
      selectedItemsToProcess.forEach((item) => {
        const segmentKey = String(item.segment || "gold")
          .trim()
          .toLowerCase();
        const assignedOperator =
          operatorsByDistrict[buyerDistrictKey]?.[segmentKey];
        if (assignedOperator?.operatorId) {
          itemsToProcess.push(item);
        } else {
          missingAssignments.push(item);
        }
      });

      const missingSegments = [
        ...new Set(
          missingAssignments.map((item) =>
            String(item.segment || "gold")
              .trim()
              .toUpperCase(),
          ),
        ),
      ];

      // If none can be routed, keep all selections and show the modal.
      if (itemsToProcess.length === 0) {
        setRequestResultModal({
          placedCount: 0,
          skippedCount: missingAssignments.length,
          missingSegments,
          district: buyerDistrict || "Unknown district",
        });
        return;
      }

      // Build updated lotDetails only for 'ready' serviceType items
      const productUpdatesMap = new Map();
      const productsToServiceTypeOrder = new Set();

      itemsToProcess.forEach((item) => {
        const isReadyItem = item.serviceType === "ready";
        if (
          isReadyItem &&
          Array.isArray(item.lotDetails) &&
          item.selectedVariants?.length > 0
        ) {
          const updatedLotDetails = item.lotDetails.map((lot) => {
            const variant = item.selectedVariants.find(
              (v) => v.size === lot.size,
            );
            if (!variant) return lot;
            const qty = num(variant.quantity);
            const newSet = Math.max(0, num(lot.set) - qty);
            return {
              ...lot,
              set: newSet,
            };
          });
          productUpdatesMap.set(item.id, {
            id: item.id,
            lotDetails: updatedLotDetails,
          });

          // Check if ALL lotDetails for this product have set === 0
          const allZero = updatedLotDetails.every((lot) => num(lot.set) === 0);
          if (allZero) {
            productsToServiceTypeOrder.add(item.id);
          }
        }
      });

      // 1. Save each item to orderList
      const orderPromises = itemsToProcess.map(async (item) => {
        const segment = item.segment?.toLowerCase() || "gold";

        // Get operator ID from area default for this district & segment
        const defaultOperator =
          operatorsByDistrict[buyerDistrictKey]?.[segment];
        // The pre-validation above guarantees this operator exists.
        const operatorId = defaultOperator.operatorId;
        const mobile = defaultOperator.mobile || null;

        const normalizedMethod =
          item.paymentMethod === "Metal" ? "Metal" : item.paymentMethod;
        const finalMethod =
          normalizedMethod === "RTGS"
            ? "RTGS"
            : paymentMethods[item.id] || "Metal";
        const updates = productUpdatesMap.get(item.id);
        const orderItem = updates
          ? { ...item, lotDetails: updates.lotDetails }
          : item;

        return addDoc(collection(db, "orderList"), {
          ...orderItem,
          buyerId: buyer.buyerId,
          operatorId,
          segment,
          mobile,
          paymentMethod: finalMethod,
          orderStatus: "Requested",
          orderTime: new Date(),
          requestHUID,
          serviceType: item.serviceType || "order",
          originalSelectionId: item.documentId || item.id,
        });
      });

      await Promise.all(orderPromises);

      // 2. Update lotDetails in products collection (only for ready items) and delete from selectedProducts
      const batch = writeBatch(db);
      itemsToProcess.forEach((item) => {
        const docIdToDelete = item.documentId || item.id;
        if (docIdToDelete) {
          batch.delete(doc(db, "selectedProducts", docIdToDelete));
        }
      });

      const productUpdates = Array.from(productUpdatesMap.values());
      productUpdates.forEach((update) => {
        if (update.id) {
          batch.update(doc(db, "products", update.id), {
            lotDetails: update.lotDetails,
          });
        }
      });

      // Update serviceType to 'order' for products where ALL lotSets became 0
      productsToServiceTypeOrder.forEach((productId) => {
        batch.update(doc(db, "products", productId), {
          serviceType: "order",
        });
      });

      await batch.commit();

      // 3. Update global context state
      itemsToProcess.forEach((item) => {
        if (item.documentId) {
          removeFromSelected(item.documentId);
        }
      });

      // 4. Reset local UI state
      setSelectedIds(new Set());
      setPaymentMethods({});
      setIsSelectionHidden(false);
      setRequestHUID(false);

      setRequestResultModal({
        placedCount: itemsToProcess.length,
        skippedCount: missingAssignments.length,
        missingSegments,
        district: buyerDistrict || "Unknown district",
      });
    } catch (error) {
      alert(error);
      alert("Failed to place request. Please try again.");
    }
  };

  const getPaymentOptions = (product) => {
    const normalizedMethod =
      product.paymentMethod === "Cash/Metal" ? "Metal" : product.paymentMethod;
    const isLockedToRTGS = normalizedMethod === "RTGS";
    const finalMethod =
      paymentMethods[product.id] || normalizedMethod || "Metal";

    return (
      <>
        <label className="payment-option">
          <input
            type="radio"
            name={`payment-${product.documentId}`}
            value="RTGS"
            checked={finalMethod === "RTGS"}
            onChange={() => handlePaymentChange(product.id, "RTGS")}
            disabled={isLockedToRTGS}
            className="visually-hidden-radio"
          />
          <span className="custom-radio"></span>
          <span className="label-text">RTGS</span>
        </label>

        <label className="payment-option">
          <input
            type="radio"
            name={`payment-${product.documentId}`}
            value="Metal"
            checked={finalMethod === "Metal"}
            onChange={() => handlePaymentChange(product.id, "Metal")}
            disabled={isLockedToRTGS}
            className="visually-hidden-radio"
          />
          <span className="custom-radio"></span>
          <span className="label-text">Metal</span>
        </label>
      </>
    );
  };

  const toggleSummaryVisibility = () => {
    setIsSelectionHidden(!isSelectionHidden);
  };

  const clearAll = async () => {
    if (selectedProducts.length === 0) return;
    if (!window.confirm("Remove all items?")) return;

    try {
      const batch = writeBatch(db);
      selectedProducts.forEach((product) => {
        if (product.documentId) {
          batch.delete(doc(db, "selectedProducts", product.documentId));
        }
      });
      await batch.commit();

      selectedProducts.forEach((product) => {
        if (product.documentId) {
          removeFromSelected(product.documentId);
        }
      });

      setSelectedIds(new Set());
      setPaymentMethods({});
      setIsSelectionHidden(false);
      setRequestHUID(false);
      clearAllSelected();
    } catch (error) {
      alert("Failed to clear selections. Please try again.");
    }
  };

  if (loading) {
    return (
      <>
        <Header title="Selections" showBack={true} />
        <SelectionsSkeleton count={3} />
      </>
    );
  }

  return (
    <>
      {requestResultModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="request-result-title"
          onClick={() => setRequestResultModal(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0, 0, 0, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "480px",
              background: "#fff",
              color: "#172033",
              borderRadius: "14px",
              padding: "24px",
              boxShadow: "0 20px 60px rgba(0,0,0,.25)",
            }}
          >
            <h2 id="request-result-title" style={{ marginTop: 0 }}>
              Request summary
            </h2>
            <p>
              {requestResultModal.placedCount > 0
                ? `${requestResultModal.placedCount} item(s) were submitted successfully.`
                : "No items were submitted because no matching operators were found."}
            </p>
            {requestResultModal.skippedCount > 0 && (
              <div
                style={{
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  borderRadius: "10px",
                  padding: "14px",
                  margin: "16px 0",
                }}
              >
                <strong>Skipped — operator not assigned</strong>
                <p style={{ marginBottom: "8px" }}>
                  District: {requestResultModal.district}
                </p>
                <p style={{ margin: 0 }}>
                  Segment(s): {requestResultModal.missingSegments.join(", ")}
                </p>
                <p style={{ marginBottom: 0, fontSize: "0.9rem" }}>
                  These items remain in your selections. Contact JMI to assign
                  an operator, then submit them again.
                </p>
              </div>
            )}
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => setRequestResultModal(null)}
                style={{
                  padding: "10px 16px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                Back to selections
              </button>
              {requestResultModal.placedCount > 0 && (
                <button
                  type="button"
                  onClick={() => navigate("/orders/requested")}
                  style={{
                    padding: "10px 16px",
                    borderRadius: "8px",
                    border: "none",
                    background: "#0B2E5B",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  View requests
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <Header title="Selections" showBack={true} />
      <div className="selections-header">
        <label className="select-all-wrapper">
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={handleSelectAll}
            disabled={selectedProducts.length === 0}
          />
          <span>Select all</span>
          <span className="count">{selectedProducts.length} items</span>
        </label>

        <button
          className="clear-selected-btn"
          onClick={clearAll}
          disabled={selectedProducts.length === 0}
        >
          Clear selected
        </button>
      </div>
      <div className="selections-container">
        <div className="products-grid">
          {selectedProducts.length === 0 ? (
            <p className="empty-state">No items in selection.</p>
          ) : (
            selectedProducts
              .filter((product) =>
                !location?.state?.serviceType
                  ? true
                  : product.serviceType === location.state.serviceType,
              )
              .map((product) => {
                const isSelected = selectedIds.has(product.documentId);
                const weightDisplay = getWeightDisplayText(product);

                return (
                  <div key={product.documentId} className="product-card">
                    <input
                      type="checkbox"
                      className={`selectns-btn ${isSelected ? "selected" : ""}`}
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectItem(product.documentId);
                      }}
                    />
                    <div className="product-image">
                      {product.images?.[0]?.url ? (
                        <img
                          src={product.images[0].url}
                          alt={product.productName}
                        />
                      ) : (
                        <div className="image-placeholder">No Image</div>
                      )}
                    </div>

                    <div className="select-product-info">
                      <span className="select-product-name">
                        {product.category}/{product.productName}
                      </span>
                      <p className="select-product-specification">
                        {product.specification}/{product.styleType}
                      </p>
                      <span style={{ fontSize: "0.8rem", fontWeight: "500" }}>
                        {product.serviceType.toUpperCase()}
                      </span>
                      {product.selectedVariants?.length > 0 && (
                        <div className="variant-display">
                          {product.selectedVariants.map((variant, index) => (
                            <span key={variant.size} className="variant-info">
                              {variant.size} x {variant.quantity}
                              {index < product.selectedVariants.length - 1
                                ? ", "
                                : ""}
                            </span>
                          ))}
                        </div>
                      )}

                      {(() => {
                        const currentItem = product;
                        const segment = (currentItem?.segment || "")
                          .toUpperCase()
                          .trim();

                        const isNetWtSegment = [
                          "GOLD",
                          "SILVER",
                          "DIAMOND",
                          "PLATINUM",
                          "BANDHEL",
                        ].includes(segment);

                        const isCaratSegment =
                          segment === "GEMS" ||
                          segment === "PEARL" ||
                          segment === "PEARLS" ||
                          segment === "GEMSTONE" ||
                          segment === "GEMSTONES";

                        const isPriceOnlySegment =
                          segment === "BRAND" ||
                          segment === "TOOL" ||
                          segment === "MACHINE" ||
                          segment === "DECOR";

                        if (isCaratSegment) {
                          const maxCaratRateOperator = parseFloat(
                            currentItem?.maxCaratRateOperator || 0,
                          );
                          const maxCaratRateJmi = parseFloat(
                            currentItem?.maxCaratRateJmi || 0,
                          );
                          const firstLot = currentItem?.lotDetails?.[0];
                          const baseCaratRate = parseFloat(
                            firstLot?.caratRate ||
                              currentItem?.maxCaratRate ||
                              0,
                          );
                          const caratRate =
                            baseCaratRate +
                            (baseCaratRate * maxCaratRateOperator) / 100 +
                            (baseCaratRate * maxCaratRateJmi) / 100;

                          const totalCaratWt = (
                            currentItem?.selectedVariants || []
                          ).reduce((sum, variant) => {
                            const lot = (currentItem?.lotDetails || []).find(
                              (l) => l.size === variant.size,
                            );
                            const qty = parseFloat(variant.quantity) || 1;
                            const wt = parseFloat(
                              lot?.caratWt ||
                                variant.caratWt ||
                                lot?.avgNetWt ||
                                variant.avgNetWt ||
                                0,
                            );
                            return sum + qty * wt;
                          }, 0);

                          const amount = totalCaratWt * caratRate;

                          return (
                            <>
                              <span className="select-product-weight">
                                Ct Wt : ~{totalCaratWt.toFixed(3)} ct
                              </span>
                              <sapn className="select-product-weight">
                                Ct Rate : ~₹{caratRate.toFixed(2)}/-
                              </sapn>
                              <p className="select-product-weight">
                                Amount : ~₹{amount.toFixed(2)}/-
                              </p>
                            </>
                          );
                        }

                        if (isPriceOnlySegment) {
                          const num = (v) => parseFloat(v) || 0;
                          const currentLot = currentItem?.lotDetails?.[0];
                          const basePrice = num(
                            currentLot?.price || currentItem?.price || 0,
                          );
                          const operatorRatePercent = num(
                            currentItem?.operatorRatePercent || 0,
                          );
                          const jmiRatePercent = num(
                            currentItem?.jmiRatePercent || 0,
                          );
                          const calculatedPrice =
                            basePrice +
                            (basePrice * operatorRatePercent) / 100 +
                            (basePrice * jmiRatePercent) / 100;
                          const totalQty = (
                            currentItem?.selectedVariants || []
                          ).reduce((sum, v) => sum + (num(v.quantity) || 1), 0);
                          const qty = totalQty > 0 ? totalQty : 1;
                          const amount = calculatedPrice * qty;

                          return (
                            <>
                              <p className="select-product-weight">
                                Price : ~₹{amount.toFixed(2)}/-
                              </p>
                            </>
                          );
                        }
                        if (isNetWtSegment) {
                          return (
                            <>
                              <p className="select-product-weight">
                                {weightDisplay.finewt}
                              </p>
                              <p className="select-product-weight">
                                {weightDisplay.mc}
                              </p>
                            </>
                          );
                        }

                        return (
                          <>
                            <p className="select-product-weight">
                              {weightDisplay.finewt}
                            </p>
                            <p className="select-product-weight">
                              {weightDisplay.mc}
                            </p>
                          </>
                        );
                      })()}
                      <p className="settleMethod">Settle via</p>
                      <div
                        className="settle-via"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {getPaymentOptions(product)}
                      </div>

                      <button
                        className="selected-remove-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveItem(product.documentId, product.id);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })
          )}
        </div>

        <div className="bottom-summary">
          <div className="summary-header">
            <div className="selected-count">
              <span>Selected:</span> <strong>{totalSelected}</strong>
            </div>
            <button
              className="toggle-selection-btn"
              onClick={toggleSummaryVisibility}
            >
              {isSelectionHidden ? "Show Selection" : "Hide Selection"}
            </button>
          </div>

          {!isSelectionHidden && (
            <div className="selection-summary">
              <h3>Selection Summary ({totalSelected} items)</h3>
              <div className="summary-table">
                <div className="summary-row">
                  <span>SETS</span>
                  <span>~{totalSets}</span>
                </div>
                <div className="summary-row">
                  <span>GR. WEIGHT</span>
                  <span>~{totalGross.toFixed(3)} g</span>
                </div>
                <div className="summary-row">
                  <span>NET WEIGHT</span>
                  <span>~{totalNet.toFixed(3)} g</span>
                </div>
                <div className="summary-row">
                  <span>FINE WEIGHT</span>
                  <span>~{totalFineWt?.toFixed(3)} g</span>
                </div>
                <div className="summary-row">
                  <span>MC</span>
                  <span>₹ {totalMC.toFixed(2)}/-</span>
                </div>
              </div>
            </div>
          )}

          <div className="request-section">
            <label className="request-huid-checkbox">
              <input
                type="checkbox"
                checked={requestHUID}
                onChange={(e) => setRequestHUID(e.target.checked)}
              />
              Req stamp/certificate
            </label>

            <button
              className="place-request-btn"
              onClick={handlePlaceRequest}
              disabled={totalSelected === 0}
            >
              Place Request
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Selections;
